import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ClientFaceDetail, ClientFaceDiffusion, DiffusionSpot, FaceDiffusion, SpotModerationFile, WancomCampaign } from '../../../park.model';
import { DiffusionDetailDialogComponent } from '../../../../../helpers/dialog/diffusion-detail-dialog/diffusion-detail-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PlayerComponent } from '../../../../spots/player/player.component';
import { WEATHER_PARAMS, WEATHER_PARAMS_ICONS } from '../../../../weather/weather.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WvSimpleSpotFile } from '../../../../spots/model/wFile';
import { environment } from '../../../../../../environments/environment';
import { Router } from '@angular/router';
import { UserAuthService } from '../../../../../services/user/user-auth.service';
import { AlertService } from '../../../../../services/alert/alert.service';
import { CampaignService } from '../../../../private-campaign/services/campaign.service';
import { MatButtonModule } from '@angular/material/button';
import { DragDropModule } from '@angular/cdk/drag-drop';

@Component({
    selector: 'app-spot-card',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        MatDialogModule,
        MatButtonModule,
        DragDropModule
    ],
    templateUrl: './spot-card.component.html',
    styleUrl: './spot-card.component.scss'
})
export class SpotCardComponent {

    //#region Variables

    /**
     * Type d'écran sur lequel le spot est diffusé
     */
    @Input() public type: 'Wancom' | 'Client' | 'Unknown';
    /**
     * Infos de la face
     */
    @Input() public face?: ClientFaceDetail;
    /**
     * Infos de la campagne (écran Wancom)
     */
    @Input() public campaign?: WancomCampaign;
    /**
     * Infos de la diffusion
     */
    @Input() public diffusion: ClientFaceDiffusion;
    /**
     * Infos du spot
     */
    @Input() public spot?: DiffusionSpot;
    /**
     * Le spot est-il le premier de la liste ?
     */
    @Input() isFirst: boolean = false;
    /**
     * Lancé lors de la suppression de la diffusion
     */
    @Output() public diffusionsDeleted = new EventEmitter<ClientFaceDiffusion>();
    /**
     * Suppression de la diffusion que pour cet écran
     */
    @Output() public diffForScreenDeleted = new EventEmitter<{ diffusion: ClientFaceDiffusion, faceId: number }>();

    public weatherParamsIcons: string[];
    public weatherParams: string[];
    private readonly baseUrl: string;
    window: Window;
    private readonly maxDayToDisplay = 100;

    //#endregion Variables

    // MARK: CONSTRUCTOR
    constructor(
        public dialog: MatDialog,
        private readonly router: Router,
        private readonly alert$: AlertService,
        private readonly campaign$: CampaignService,
        private readonly datePipe: DatePipe,
    ) {
        this.weatherParamsIcons = WEATHER_PARAMS_ICONS;
        this.weatherParams = WEATHER_PARAMS;
        this.baseUrl = environment.apiBaseUrl;
    }

    public seeDiffDetail(diffusion) {
        const detailDialogRef = this.dialog.open(DiffusionDetailDialogComponent);
        detailDialogRef.componentInstance.diffusionId = diffusion.diffusionId;
        detailDialogRef.componentInstance.diffusionName = diffusion.name;
    }

    public getBackgroundImageUrl(spotFile: WvSimpleSpotFile, isMediaLibrary: boolean = false) {
        return "url(" + this.getSpotFileURL(spotFile, isMediaLibrary) + ")";
    }

    public getSpotFileURL(
        spotFile: WvSimpleSpotFile,
        isMediaLibrary: boolean = false
    ) {
        return encodeURI(
            `${this.baseUrl}/file/${isMediaLibrary ? 'getMediaLibraryFile' : 'getFile'}/${spotFile?.thumbnailStorageId}/${spotFile?.thumbnailStorageId}`
        );
    }

    playFile(file: any) {
        if (
            file.extensionType !== 8 &&
            file.storageId.indexOf('.json') === -1
        ) {
            const dialogRef = this.dialog.open(PlayerComponent, {});
            dialogRef.componentInstance.fileStorageId = file.storageId;

            dialogRef.componentInstance.loadVideo();
        } else {
            this.playJsonFile(file);
        }
    }

    changeCreative(diffusion: any, duration?: any) {
        const selectedSpotId: number = diffusion.spots[0]?.spotId ?? null;

        let url: string = `spots/selectmethod?diffusionId=${diffusion.diffusionId}`;
        url += selectedSpotId ? `&spotId=${selectedSpotId}` : '';
        url += duration != null ? `&duration=${duration}` : '';

        this.router.navigateByUrl(url);
    }

    private playJsonFile(file: any) {
        const dialogRef = this.dialog.open(PlayerComponent, {});
        dialogRef.componentInstance.fileStorageId = file.storageId;
        file.videoStorageId = file.storageId;
        dialogRef.componentInstance.file = file;
        dialogRef.componentInstance.loadJson();
    }

    public getBackgroundURL(moderationFile: SpotModerationFile) {
        return encodeURI(
            `${this.baseUrl}/file/getFile/${moderationFile.thumbnailStorageId}/${moderationFile.thumbnailStorageId}`
        );
    }

    public editDiffusionParams(diffusionId: number) {
        this.router.navigateByUrl(
            `private-campaign/${UserAuthService.user?.organizationId}?diffusionId=${diffusionId}`
        );
    }

    public editWancomDiffParams(diffusionId: number): void {
        this.router.navigateByUrl(
            `private-campaign/?DiffToEditId=${diffusionId}`
        );
    }

    public duplicateDiffusion(campaignId: number) {
        this.campaign$.duplicateCampaign(campaignId).subscribe({
            next: () => {
                this.alert$.show({
                    message: 'Diffusion dupliquée !',
                    duration: 500,
                });
                this.window.location.reload();
            },
            error: (err) => {
                console.error(err);

                this.alert$.show({
                    message: 'Erreur lors de la duplication de votre diffusion',
                    duration: 1500,
                });
            },
        });
    }

    public deleteDiffusion(diffusion: ClientFaceDiffusion) {
        this.diffusionsDeleted.emit(diffusion);
    }

    public deleteDiffForScreen(diffusion: ClientFaceDiffusion, faceId: number) {
        this.diffForScreenDeleted.emit({ diffusion: diffusion, faceId: faceId });
    }

    public editScreensDiffusion(diffusionId: number) {
        this.router.navigateByUrl(
            `private-campaign/screens/${diffusionId}?editMode=true&fromPark=true&organizationId=${UserAuthService.user?.organizationId}`
        );
    }

    public getDeltaItemClass(diffusion: FaceDiffusion): string {
        const dayDeltaText: string = this.getDayDelta(diffusion);

        const findTerm = (term) => {
            if (dayDeltaText.includes(term)) {
                return dayDeltaText;
            }
        };

        let classStr: string = 'deltaItem ';

        switch (dayDeltaText) {
            case findTerm('Commence demain'):
                classStr += 'verysoon';
                break;
            case findTerm('Commence dans'):
            case findTerm('Terminée'):
            case findTerm('Commence le'):
                classStr += 'soon';
                break;
            case findTerm('prévue demain'):
                classStr += 'endTomorrow';
                break;
            case findTerm('En cours'):
                classStr += 'current';
                break;
            case findTerm('prévue ce soir'):
                classStr += 'endNow';
                break;
            default:
                break;
        }

        return classStr;
    }

    /**
     * Calcule le temps avant que la diffusion démarre ou que la diffusion se termine
     * @param diffusion Diffusion à retirer les jours
     * @returns Un texte à afficheir sur la diffusion
     */
    getDayDelta(diffusion: FaceDiffusion): string {
        let temporaryStartDate = diffusion.startDate.toString() + 'Z';
        let temporaryEndDate = diffusion.endDate.toString() + 'Z';
        const startDate = new Date(temporaryStartDate);
        const endDate = new Date(temporaryEndDate);

        let today: Date = new Date();
        today.setUTCHours(0, 0, 0, 0);

        let deltaStartMs: number = today.valueOf() - startDate.valueOf();
        let returnString: string = '';
        if (typeof deltaStartMs === 'number') {
            const deltaStartDay: number = Math.abs(
                Math.trunc(deltaStartMs / 86400000)
            );
            if (deltaStartMs < 0 && deltaStartDay !== 0) {

                if (deltaStartDay === 1) {
                    returnString = 'Commence demain 🔥';
                } else if (deltaStartDay <= this.maxDayToDisplay) {
                    returnString = `Commence dans ${deltaStartDay} jours`;
                } else {
                    returnString = `Commence le ${this.datePipe.transform(
                        diffusion.startDate,
                        'dd/MM/yyyy'
                    )}`;
                }

                // console.log(returnString);
            } else {
                //Date passée 'Fin prévue dans ...'
                returnString = this.getEndDeltaDay(
                    endDate,
                    today,
                    returnString,
                    diffusion
                );
            }
        }

        return returnString;
    }

    private getEndDeltaDay(
        endDate: Date,
        today: Date,
        returnString: string,
        diffusion: FaceDiffusion
    ): string {
        const deltaEndDays: number =
            (endDate.valueOf() - today.valueOf()) / 86400000
            ;

        const truncedDeltaEndD = Math.trunc(deltaEndDays);



        if (deltaEndDays >= 0) {
            switch (truncedDeltaEndD) {
                case 0:

                    returnString = `Fin prévue ce soir`;
                    break;
                case 1:
                    returnString = 'Fin prévue demain';
                    break;

                default:
                    if (truncedDeltaEndD <= this.maxDayToDisplay) {
                        returnString = `En cours, reste ${truncedDeltaEndD} jours`;
                    } else if (diffusion.isPermanent) {
                        returnString = 'En cours';
                    } else {
                        returnString = `En cours, termine le ${this.datePipe.transform(
                            diffusion.endDate,
                            'dd/MM/yyyy'
                        )}`;
                    }
                    break;
            }
        } else if (truncedDeltaEndD === 0) {
            returnString = `Terminée hier`;
        } else {
            returnString = `Terminée depuis ${Math.abs(truncedDeltaEndD)} jour${truncedDeltaEndD === -1 ? '' : 's'}`;
        }


        return returnString;
    }

    public isLightDiffusionIconsClass(): boolean {
        return this.isFirst &&
            !this.diffusion.trigger.paramWeather &&
            !this.diffusion.trigger.minTemp &&
            !this.diffusion.trigger.maxTemp &&
            !this.diffusion.trigger.paramTempCondition &&
            !this.diffusion.trigger.paramTemp &&
            (this.diffusion.trigger.diffusionDay.length === 0 ||
                this.diffusion.trigger.diffusionDay == null) &&
            (!this.diffusion.trigger.paramEndHour ||
                this.diffusion.trigger.paramEndHour == null) &&
            (!this.diffusion.trigger.paramStartHour ||
                this.diffusion.trigger.paramStartHour == null)
    }

    public editSpotFromTemplate(diff: FaceDiffusion, faceId: any) {
        const myQueryParams = [
            {
                id: diff.spots[0].templateRefenceId,
                edit: true,
                faceId: faceId
            },
        ];

        this.router.navigateByUrl(
            `spots/generator?option=${JSON.stringify(myQueryParams)}&spotId=${diff.spots[0].spotId
            }` +
            (diff.diffusionId != null
                ? `&diffusionId=${diff.diffusionId}`
                : '')
        );
    }

    getHour(number){
        let readableHour = null; // Initialize the variable to store the readable hour
        if (number) {
            for (let i = 0; i < 24; i++) {
                if (number & (1 << i)) { // Check if the bit corresponding to the hour is set
                    readableHour = i; // Assign the hour
                    break;
                }
            }
        }
        return readableHour;
    }
}
